import { useEffect, useState } from 'react'

/**
 * Returns a `boolean` indicating whether the browser matches the `prefers-reduced-motion` media query.
 *
 * @example
 * Basic usage
 *
 * ```ts
 * const reducedMotion = useReducedMotion()
 * //    ^? boolean
 * ```
 *
 * @example
 * Changing the initial value
 *
 * The media query check cannot be performed during server-side rendering; instead, the hook returns an initial value.
 * Once hydration occurs, this value is updated to reflect the actual media query state.
 *
 * You can configure the initial value by passing a `boolean` argument.
 *
 * Defaults to `false`.
 *
 * ```
 * const reducedMotion = useReducedMotion(true)
 * //    ^? boolean
 * ```
 */
export const useReducedMotion = (initialValue = false): boolean => {
	const [reducedMotion, setReducedMotion] = useState(initialValue)

	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

		const queryChangeHandler = (event: MediaQueryListEvent) => {
			setReducedMotion(event.matches)
		}

		if (mediaQuery) {
			setReducedMotion(mediaQuery.matches)
			mediaQuery.addEventListener('change', queryChangeHandler)

			return () => mediaQuery.removeEventListener('change', queryChangeHandler)
		}

		return () => {}
	}, [])

	return reducedMotion
}
